import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import CompanyAdministratorUsersTable from './CompanyUsersTable';
import classNames from 'classnames';
import useRequest from 'hooks/useRequest';
import JobFunction from 'store/types/JobFunction';
import UserService from 'services/api/UserService';
import CompanyService from 'services/api/CompanyService';
import CompanyUser from 'store/types/CompanyUser';
import Alert from '@mui/material/Alert';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { Button, Grid } from '@mui/material';
import { Add } from '@mui/icons-material';
import Spinner from 'components/shared/Spinner';
import AddCompanyUserModal from './AddCompanyUserModal';
import { CompanyEmployeeAccessRequestsData } from 'store/types/CompanyEmployeeAccessRequest';
import LinkCompanyUserModal from './LinkCompanyUserModal';
import { ParametersContext } from 'components/ParametersGuard';
import { CompanyPaymentDetails } from 'store/types/Company';
import SelectOption from 'store/types/SelectOption';
import { getAvailablePortalRoles } from 'util/User';
import { UserContext } from 'components/UserGuard';

import commonStyles from 'styles/common.module.scss';
import pageStyles from '../CompanyPageView/CompanyPageView.module.scss';
import styles from './CompanyUsersSection.module.scss';

interface CompanyUsersSectionProps {
  companyId: string;
  refetchCount: () => void;
}

const CompanyUsersSection: React.FunctionComponent<CompanyUsersSectionProps> = ({ companyId, refetchCount }) => {
  const {
    companyManagement: { adminsTableHeader },
  } = useContext(ParametersContext);
  const { functions } = useContext(UserContext);
  const { data, loading, error } = useRequest<JobFunction[]>(UserService.getJobFunctionList);
  const companyUsersRequest = useMemo(
    () => (companyId ? () => CompanyService.getCompanyAdministratorUsers(companyId) : undefined),
    [companyId]
  );
  const companyEmployeeAccessRequests = useMemo(
    () => (companyId ? () => CompanyService.getCompanyEmployeeAccessRequests(companyId) : undefined),
    [companyId]
  );
  const companyDetailsRequest = useMemo(
    () => (companyId ? () => CompanyService.getCompanyPaymentDetails(companyId) : undefined),
    [companyId]
  );
  const companyAdminsRequestData = useRequest<CompanyUser[]>(companyUsersRequest);
  const companyEmployeeAccessRequestsData =
    useRequest<CompanyEmployeeAccessRequestsData>(companyEmployeeAccessRequests);
  const {
    data: companyDetailsData,
    loading: companyDetailsLoading,
    error: companyDetailsError,
  } = useRequest<CompanyPaymentDetails>(companyDetailsRequest);
  const { data: adminData, refetch: refetchAdmins } = companyAdminsRequestData;
  const { data: employeeAccessRequests } = companyEmployeeAccessRequestsData;
  const { data: directoryStatusOptions } = useRequest<SelectOption[]>(CompanyService.getCompanyDirectoryStatusOptions);
  const [addUserModalOpen, setAddUserModalOpen] = useState<boolean>(false);
  const [linkUserModalOpen, setLinkUserModalOpen] = useState<boolean>(false);
  const [showJobFunctionDropdown, setShowDropFunctionDropdown] = useState<boolean>(false);
  const [portalRoles, setPortalRoles] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (data?.length) {
      const userRoles: string[] = functions?.find((company) => company.companyId === companyId)?.jobFunctionNames || [];
      const portalRoles: SelectOption[] = getAvailablePortalRoles(data, userRoles);

      setPortalRoles(portalRoles);
    }
  }, [companyId, data, functions]);

  const refetchRequests = useCallback(() => {
    refetchAdmins();
    refetchCount();
  }, [refetchAdmins, refetchCount]);

  const handleAddUserModalOpen = useCallback(() => {
    setAddUserModalOpen(true);
    setLinkUserModalOpen(false);
  }, []);

  const handleAddUserModalClose = useCallback(() => {
    setAddUserModalOpen(false);
  }, []);

  const handleLinkUserModalOpen = useCallback(() => {
    setLinkUserModalOpen(true);
    setAddUserModalOpen(false);
  }, []);

  const handleLinkUserModalClose = useCallback(() => {
    setLinkUserModalOpen(false);
  }, []);

  const handleAddAdministrator = useCallback(() => {
    setShowDropFunctionDropdown(true);
    handleLinkUserModalOpen();
  }, [handleLinkUserModalOpen]);

  return (
    <Spinner loading={loading || companyDetailsLoading} transparent={false}>
      {error || companyDetailsError ? (
        <Alert severity={'error'} className={commonStyles.alert}>
          {error || companyDetailsError}
        </Alert>
      ) : (
        <Grid {...defaultGridContainerProps}>
          <Grid {...defaultGridItemProps} xs={true} className={pageStyles.wrapper}>
            <h2 className={classNames(commonStyles.subTitle, commonStyles.uppercase, pageStyles.title)}>
              {'Company Management'}
            </h2>
          </Grid>
          <Grid {...defaultGridItemProps} md={'auto'}>
            <div className={styles.addAdminButton}>
              <Button
                color={'secondary'}
                variant={'contained'}
                className={styles.iconButton}
                onClick={handleAddAdministrator}
              >
                Add Person <Add />
              </Button>
            </div>
          </Grid>
          <Grid {...defaultGridItemProps}>
            {adminsTableHeader && <p dangerouslySetInnerHTML={{ __html: adminsTableHeader }} />}
            <CompanyAdministratorUsersTable
              {...companyAdminsRequestData}
              companyId={companyId}
              jobFunctionsList={data || []}
              companyRequests={employeeAccessRequests}
              memberClassification={companyDetailsData?.memberClassification}
              directoryStatusOptions={directoryStatusOptions}
            />
          </Grid>
        </Grid>
      )}
      {linkUserModalOpen && (
        <LinkCompanyUserModal
          companyMembers={adminData}
          companyRequests={employeeAccessRequests}
          open={linkUserModalOpen}
          onClose={handleLinkUserModalClose}
          onAddUserModalOpen={handleAddUserModalOpen}
          companyId={companyId}
          portalRolesList={portalRoles || []}
          refetch={refetchRequests}
        />
      )}
      {addUserModalOpen && (
        <AddCompanyUserModal
          predefinedAddress={companyDetailsData?.address}
          memberClassification={companyDetailsData?.memberClassification}
          directoryStatusOptions={directoryStatusOptions}
          open={addUserModalOpen}
          onClose={handleAddUserModalClose}
          showJobFunctionDropdown={showJobFunctionDropdown}
          companyId={companyId}
          portalRolesList={portalRoles || []}
          refetch={refetchRequests}
        />
      )}
    </Spinner>
  );
};

export default CompanyUsersSection;
